import { lazy } from "react";
import { Navigate } from "react-router-dom";
import ProtectedRoute from "../guards/ProtectedRoute";
import { LAYOUT_IDENTIFIERS, MODULE_PATHS } from "../../utils/moduleConfig";

const MainLayout = lazy(() => import("../../layouts/MainLayout/MainLayout"));
const AccountPage = lazy(() =>
  import("../../Modules/FinanceManager/Accounts/Page/AccountPage")
);
const AllBudgetPage = lazy(() =>
  import("../../Modules/FinanceManager/Budget/Page/AllBudgetPage")
);
const BudgetTransactionPage = lazy(() =>
  import("../../Modules/FinanceManager/Budget/Page/BudgetTransactionPage")
);
const NewBudgetDetailsView = lazy(() =>
  import("../../Modules/FinanceManager/Budget/Page/NewBudgetDetailsView")
);
const FMDashboard = lazy(() =>
  import("../../Modules/FinanceManager/Dashboard/Page/FMDashboard")
);
const FinancialPlanForm = lazy(() =>
  import("../../Modules/FinanceManager/FinancialPlanning/Page/FinancialPlanForm")
);
const FinancialPlanning = lazy(() =>
  import("../../Modules/FinanceManager/FinancialPlanning/Page/FinancialPlanningPage")
);
const FinancialPlanViewPage = lazy(() =>
  import("../../Modules/FinanceManager/FinancialPlanning/Page/FinancialPlanViewPage")
);
const GoalsDashboard = lazy(() =>
  import("../../Modules/FinanceManager/SavingGoals/Page/GoalsDashboard")
);
const GoalsOverview = lazy(() =>
  import("../../Modules/FinanceManager/SavingGoals/Page/GoalsOverview")
);
const SavingsGoalPage = lazy(() =>
  import("../../Modules/FinanceManager/SavingGoals/Page/SavingGoalPage")
);
const LoansPage = lazy(() =>
  import("../../Modules/FinanceManager/Loans/Page/LoansPage")
);
const LoanOverviewPage = lazy(() =>
  import("../../Modules/FinanceManager/Loans/Page/LoanOverviewPage")
);
const AccountTransactionPage = lazy(() =>
  import("../../Modules/FinanceManager/Transactions/Page/AccountTransactionPage")
);
const TransactionsPage = lazy(() =>
  import("../../Modules/FinanceManager/Transactions/Page/TransactionsPage")
);
const ReportsLayout = lazy(() =>
  import("../../Modules/FinanceManager/Reports/Page/ReportsLayout")
);
const TrialBalancePage = lazy(() =>
  import("../../Modules/FinanceManager/Reports/Page/TrialBalancePage")
);
const BalanceSheetPage = lazy(() =>
  import("../../Modules/FinanceManager/Reports/Page/BalanceSheetPage")
);
const IncomeStatementPage = lazy(() =>
  import("../../Modules/FinanceManager/Reports/Page/IncomeStatementPage")
);

// ProtectedRoute on the layout element guards every child automatically.
// Child-level ProtectedRoute wrappers are removed — they were redundant
// and caused a loading-flash on every page navigation.
const financeManagerRoutes = {
  path: MODULE_PATHS.financeManager,
  element: (
    <ProtectedRoute>
      <MainLayout identifier={LAYOUT_IDENTIFIERS.financeManager} />
    </ProtectedRoute>
  ),
  children: [
    {
      index: true,
      element: <Navigate to="dashboard" replace />,
    },
    { path: "dashboard",  element: <FMDashboard /> },

    {
      path: "budget",
      children: [
        { index: true,        element: <AllBudgetPage /> },
        { path: ":BudgetId/:name", element: <NewBudgetDetailsView /> },
        { path: ":name",      element: <NewBudgetDetailsView /> },
        { path: ":BudgetId/transaction/:id", element: <BudgetTransactionPage /> },
      ],
    },

    {
      path: "saving-goal",
      children: [
        { index: true,        element: <SavingsGoalPage /> },
        { path: "dashboard",  element: <GoalsDashboard /> },
        { path: ":id",        element: <GoalsOverview /> },
      ],
    },

    {
      path: "financial-planning",
      children: [
        { index: true,       element: <FinancialPlanning /> },
        { path: "plan",      element: <FinancialPlanForm /> },
        { path: ":planId",   element: <FinancialPlanViewPage /> },
      ],
    },

    {
      path: "accounts",
      children: [
        { index: true, element: <AccountPage /> },
        { path: ":name", element: <AccountTransactionPage /> },
      ],
    },

    { path: "transactions", element: <TransactionsPage /> },

    {
      path: "reports",
      element: <ReportsLayout />,
      children: [
        { index: true, element: <Navigate to="trial-balance" replace /> },
        { path: "trial-balance", element: <TrialBalancePage /> },
        { path: "balance-sheet", element: <BalanceSheetPage /> },
        { path: "income-statement", element: <IncomeStatementPage /> },
      ],
    },

    {
      path: "loans",
      children: [
        { index: true, element: <LoansPage /> },
        { path: ":id", element: <LoanOverviewPage /> },
      ],
    },
  ],
};

export default financeManagerRoutes;
